import { Avatar } from "./Avatar";
import { Text } from "@twilio-paste/core";
import { Menu, MenuButton, useMenuState, MenuItem } from "@twilio-paste/menu";
import { ChevronDownIcon } from "@twilio-paste/icons/esm/ChevronDownIcon";
import { SearchIcon } from "@twilio-paste/icons/esm/SearchIcon";
import React, { useMemo, useState } from "react";
import styles from "../styles";
import { Client, ConnectionState, User } from "@twilio/conversations";
import UserProfileModal from "./modals/UserProfileModal";
import { readUserProfile } from "../api";
import { AppLogo, LOGO_SUB_TITLE, LOGO_TITLE } from "../branding";
import { useSelector } from "react-redux";
import { AppState } from "../store";
import { getTranslation } from "./../utils/localUtils";
import { InstallPromptState } from "../hooks/useInstallPrompt";

type AppHeaderProps = {
  user: string;
  onSignOut: () => void;
  connectionState: ConnectionState;
  client?: Client;
  installPrompt: InstallPromptState;
  notificationPermission: NotificationPermission | "unsupported";
  onEnableNotifications: () => void;
  messageAlertSoundEnabled: boolean;
  onToggleMessageAlertSound: () => void;
  onTestMessageAlertSound: () => void;
  isMobile?: boolean;
  onMobilePrimaryAction?: () => void;
  showMobilePrimaryAction?: boolean;
};
const AppHeader: React.FC<AppHeaderProps> = ({
  user,
  onSignOut,
  connectionState,
  client,
  installPrompt,
  notificationPermission,
  onEnableNotifications,
  messageAlertSoundEnabled,
  onToggleMessageAlertSound,
  onTestMessageAlertSound,
  isMobile = false,
  onMobilePrimaryAction,
  showMobilePrimaryAction = false,
}) => {
  const menu = useMenuState();

  const [showUserProfileModal, setUserProfileModal] = useState(false);

  const [userProfile, setUserProfile] = useState<User | undefined>(undefined);

  const handleUserProfileModalClose = () => setUserProfileModal(false);

  const local = useSelector((state: AppState) => state.local);
  const online = getTranslation(local, "online");
  const connecting = getTranslation(local, "connecting");
  const offline = getTranslation(local, "offline");
  const signout = getTranslation(local, "signout");
  const userProfileTxt = getTranslation(local, "userProfileTxt");
  let notificationActionLabel = "Enable push notifications";
  if (installPrompt.requiresHomeScreenForPush) {
    notificationActionLabel = "Install app for push notifications";
  } else if (notificationPermission === "denied") {
    notificationActionLabel = "Open notification settings";
  }

  let notificationStatusLabel = "Enable notifications for mobile alerts.";
  if (installPrompt.requiresHomeScreenForPush) {
    notificationStatusLabel = "Push on iPhone works from the Home Screen app.";
  } else if (notificationPermission === "granted") {
    notificationStatusLabel = "Push notifications enabled";
  } else if (notificationPermission === "unsupported") {
    notificationStatusLabel = "Push notifications unavailable";
  } else if (notificationPermission === "denied") {
    notificationStatusLabel = "Notifications are blocked in browser settings.";
  }

  const label: "online" | "connecting" | "offline" = useMemo(() => {
    switch (connectionState) {
      case "connected":
        return "online";
      case "connecting":
        return "connecting";
      default:
        return "offline";
    }
  }, [connectionState]);

  const handleUserProfileModalOpen = async () => {
    const userProfileTemp = await readUserProfile(user, client);
    setUserProfile(userProfileTemp);
    setUserProfileModal(true);
  };

  const statusColor =
    label === "online"
      ? "#2ED47A"
      : label === "connecting"
      ? "#F5A623"
      : "#7F8AA3";

  const statusLabel =
    label === "online"
      ? online
      : label === "connecting"
      ? `${connecting}...`
      : offline;

  const mobileActionButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: 12,
    border: "1px solid rgba(220, 232, 250, 0.18)",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    boxShadow: "0 10px 24px rgba(7, 21, 40, 0.18)",
    cursor: "pointer",
  } as const;

  return (
    <div
      style={{
        ...styles.appHeader,
        flexDirection: "row",
        alignItems: "center",
        gap: isMobile ? 10 : 16,
        padding: isMobile ? "12px 14px" : styles.appHeader.padding,
        flexWrap: "nowrap",
      }}
    >
      <div
        style={{
          ...styles.flex,
          alignItems: "center",
          minWidth: 0,
          flex: 1,
          gap: isMobile ? 12 : 0,
        }}
      >
        <div
          style={{
            ...styles.appLogoWrapper,
            width: isMobile ? 36 : styles.appLogoWrapper.width,
            height: isMobile ? 36 : styles.appLogoWrapper.height,
            borderRadius: isMobile ? 10 : styles.appLogoWrapper.borderRadius,
            flexShrink: 0,
          }}
        >
          <AppLogo />
        </div>
        <div
          style={{
            ...styles.appLogoTitle,
            paddingLeft: isMobile ? 0 : styles.appLogoTitle.paddingLeft,
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: isMobile ? 15 : styles.appLogoTitle.fontSize,
              lineHeight: isMobile ? "20px" : styles.appLogoTitle.lineHeight,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {LOGO_TITLE}
          </div>
          {isMobile ? (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginTop: 4,
                padding: "2px 8px",
                borderRadius: 999,
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                color: "#D9E7FF",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: "16px",
                letterSpacing: "0.04em",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: statusColor,
                  display: "inline-block",
                  boxShadow: `0 0 0 3px ${statusColor}22`,
                }}
              />
              {statusLabel}
            </div>
          ) : (
            <div style={styles.appLogoSubTitle}>{LOGO_SUB_TITLE}</div>
          )}
        </div>
      </div>
      {isMobile ? (
        <div
          style={{
            ...styles.flex,
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          {showMobilePrimaryAction ? (
            <button
              type="button"
              onClick={onMobilePrimaryAction}
              style={mobileActionButtonStyle}
              aria-label="Search conversations"
            >
              <SearchIcon
                color="colorTextInverse"
                decorative={false}
                title="Search conversations"
              />
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleUserProfileModalOpen}
            style={mobileActionButtonStyle}
            aria-label="Open profile"
          >
            <Avatar name={user} size="sizeIcon40" />
          </button>
          <MenuButton
            {...menu}
            variant="link"
            size="reset"
            style={mobileActionButtonStyle}
          >
            <ChevronDownIcon
              color="colorTextInverse"
              decorative={false}
              title="Quick actions"
            />
          </MenuButton>
        </div>
      ) : (
        <div
          style={{
            ...styles.userTile,
            maxWidth: "100%",
          }}
        >
          <Avatar name={user} />
          <div
            style={{
              padding: "0 10px",
              minWidth: 0,
            }}
          >
            <Text
              as="span"
              style={{
                ...styles.userName,
                maxWidth: "28vw",
              }}
            >
              {user}
            </Text>
            <Text
              as="span"
              color={
                label === "online"
                  ? "colorTextPrimaryWeak"
                  : label === "connecting"
                  ? "colorTextIconBusy"
                  : "colorTextWeaker"
              }
              style={styles.userStatus}
            >
              {statusLabel}
            </Text>
          </div>
          <MenuButton {...menu} variant="link" size="reset">
            <ChevronDownIcon
              color="colorTextInverse"
              decorative={false}
              title="Settings"
            />
          </MenuButton>
        </div>
      )}
      <Menu {...menu} aria-label="Preferences">
        <MenuItem {...menu} disabled>
          {notificationStatusLabel}
        </MenuItem>
        {notificationPermission !== "granted" ? (
          <MenuItem {...menu} onClick={onEnableNotifications}>
            {notificationActionLabel}
          </MenuItem>
        ) : null}
        {installPrompt.canInstall ? (
          <MenuItem {...menu} onClick={installPrompt.promptInstall}>
            {installPrompt.installLabel}
          </MenuItem>
        ) : null}
        <MenuItem {...menu} onClick={onToggleMessageAlertSound}>
          {messageAlertSoundEnabled
            ? "Mute sound alerts"
            : "Enable sound alerts"}
        </MenuItem>
        {messageAlertSoundEnabled ? (
          <MenuItem {...menu} onClick={onTestMessageAlertSound}>
            Test sound alert
          </MenuItem>
        ) : null}
        <MenuItem {...menu} onClick={onSignOut}>
          {signout}
        </MenuItem>
        <MenuItem {...menu} onClick={handleUserProfileModalOpen}>
          {userProfileTxt}
        </MenuItem>
      </Menu>
      {showUserProfileModal && (
        <UserProfileModal
          isModalOpen={showUserProfileModal}
          handleClose={handleUserProfileModalClose}
          user={userProfile}
        ></UserProfileModal>
      )}
    </div>
  );
};

export default AppHeader;
